import {useRef, type CSSProperties, type ReactNode} from "react";
import {motion, useInView, useReducedMotion} from "motion/react";

import {
    LANDING_HOVER_LIFT,
    LANDING_HOVER_SPRING,
    LANDING_LIT,
    LANDING_STAGGER_CARDS,
    LANDING_VIEWPORT,
    blockVariants,
    groupVariants,
    itemVariants,
} from "./motion";
import {useScrolledPast} from "./useScrolledPast";

/**
 * Появление блоков лендинга.
 *
 * Зачем отдельный компонент, а не `motion.div` по месту: на странице девять
 * секций, и если в каждой писать пружины руками, через месяц их станет девять
 * разных. Здесь одна точка, где живут значения (`./motion.ts`), и одна ветка
 * «уменьшить движение» — её нельзя забыть проставить.
 *
 * Как это работает (скилл apple-design):
 *   • §7 «spatial consistency» — все блоки приезжают снизу вверх, то есть
 *     оттуда, откуда их выносит прокрутка. Разнонаправленных выездов на
 *     странице нет: пять разных сторон появления читаются как аттракцион.
 *   • §4 «behavior over animation» — пружина, а не keyframes: если человек
 *     проскроллил дальше в середине появления, движение продолжается от текущей
 *     точки, а не дёргается к цели.
 *   • §14 — при системной настройке «уменьшить движение» сдвиг исчезает
 *     целиком, остаётся проявление 0.18 с.
 *
 * Появление одноразовое (`once: true`): блок, который прячется и появляется
 * снова при каждой прокрутке вверх-вниз, — это мигание, а не жизнь страницы.
 */

const MOTION_TAGS = {
    div: motion.div,
    section: motion.section,
    header: motion.header,
    footer: motion.footer,
    aside: motion.aside,
    ul: motion.ul,
    ol: motion.ol,
    li: motion.li,
    p: motion.p,
    span: motion.span,
    h1: motion.h1,
    h2: motion.h2,
    h3: motion.h3,
} as const;

export type RevealTag = keyof typeof MOTION_TAGS;

interface RevealProps {
    /** Какой тег отрисовать. По умолчанию `div` — разметка секций не меняется. */
    as?: RevealTag;
    className?: string;
    id?: string;
    children?: ReactNode;
    "aria-hidden"?: boolean;
    "aria-label"?: string;
    /**
     * block — блок приезжает сам;
     * group — контейнер невидим, только задаёт очередь детям (сетка карточек);
     * item  — элемент внутри блока или сетки, очередь приходит от родителя.
     */
    variant?: "block" | "group" | "item";
    /**
     * scroll  — по прокрутке (по умолчанию);
     * mount   — сразу при загрузке: первый экран человек не «прокручивает» к себе;
     * inherit — ничего не запускает само, ждёт команды от родителя.
     */
    trigger?: "scroll" | "mount" | "inherit";
    /** Разбежка детей, с. Имеет смысл для block и group. */
    stagger?: number;
    /** Задержка старта, с. Для очереди карточек — `cardDelay(index)` из `./motion`. */
    delay?: number;
    /** Откуда приезжает блок. Сверху — только шапка, она там и живёт. */
    from?: "below" | "above";
    /** Подъём под указателем. На тач-экране не срабатывает — там наведения нет. */
    hover?: boolean;
    /**
     * Блик по карточке при наведении (только точный указатель, см. `.rl-shine` в
     * `styles/landing.css`). Добавляет в конец карточки один абсолютно
     * позиционированный декоративный элемент: поток, семантику и текст не трогает.
     */
    shine?: boolean;
    /**
     * Подсветка по прокрутке: когда блок оказывается в середине экрана, ставится
     * `data-lit`. Число — номер в очереди (шаг 0, 1, 2…), от него считается
     * задержка (`LANDING_LIT.gap`). Одноразово. Сам эффект рисует CSS.
     */
    lit?: number;
}

export function Reveal({
    as = "div",
    className,
    id,
    children,
    variant = "block",
    trigger = "scroll",
    stagger = 0,
    delay = 0,
    from = "below",
    hover = false,
    shine = false,
    lit,
    ...aria
}: RevealProps) {
    const reduceMotion = Boolean(useReducedMotion());
    const Tag = MOTION_TAGS[as] as unknown as typeof motion.div;

    // Блок, мимо которого пролистали быстрее, чем браузер успел его заметить,
    // иначе остался бы пустым навсегда — см. `./useScrolledPast.ts`.
    const ref = useRef<HTMLDivElement | null>(null);
    const scrolledPast = useScrolledPast(ref, trigger === "scroll");

    const variants =
        variant === "item"
            ? itemVariants(reduceMotion, delay)
            : variant === "group"
              ? groupVariants(reduceMotion, stagger || LANDING_STAGGER_CARDS)
              : blockVariants(reduceMotion, stagger, delay, from);

    // Наведение — только на точном указателе и только когда движение не просили
    // убавить. Motion сам не считает касание наведением, поэтому «залипшего
    // ховера» после тапа на телефоне не будет.
    const hoverProps =
        hover && !reduceMotion ? ({whileHover: {y: -LANDING_HOVER_LIFT}, transition: LANDING_HOVER_SPRING} as const) : {};

    // Подсветка шага. Хук вызывается всегда (правила хуков), но следит только
    // когда `lit` задан; блок, мимо которого пролистали, подсвечиваем сразу.
    const inMiddle = useInView(ref, {once: true, margin: LANDING_LIT.margin});
    const isLit = lit !== undefined && (inMiddle || scrolledPast);
    const litProps =
        lit === undefined
            ? {}
            : {
                  "data-lit": isLit ? "" : undefined,
                  style: {"--rl-lit-delay": `${lit * LANDING_LIT.gap}s`} as CSSProperties,
              };

    const triggerProps =
        trigger === "mount"
            ? ({initial: "hidden", animate: "shown"} as const)
            : trigger === "scroll"
              ? ({
                    initial: "hidden",
                    whileInView: "shown",
                    viewport: LANDING_VIEWPORT,
                    // Блок уехал за верхнюю кромку непоказанным — показываем его
                    // без ожидания кадра наблюдателя. Движение при этом играет
                    // как обычно: оно происходит выше экрана, и к моменту, когда
                    // человек долистает обратно, блок уже стоит на месте.
                    ...(scrolledPast ? {animate: "shown"} : {}),
                } as const)
              : {};

    return (
        <Tag ref={ref} className={className} id={id} variants={variants} {...triggerProps} {...hoverProps} {...litProps} {...aria}>
            {children}
            {shine && !reduceMotion ? (
                <span className="rl-shine" aria-hidden>
                    <i/>
                </span>
            ) : null}
        </Tag>
    );
}

/** Короткая запись для элемента внутри уже появляющегося блока. */
export function RevealItem(props: Omit<RevealProps, "variant" | "trigger">) {
    return <Reveal {...props} variant="item" trigger="inherit"/>;
}

/**
 * Карточка в сетке: следит за кадром сама, очередь — задержкой по номеру.
 * Почему не дирижёром из контейнера — расписано у `cardDelay` в `./motion.ts`.
 */
export function RevealCard(props: Omit<RevealProps, "variant" | "trigger">) {
    return <Reveal {...props} variant="item" trigger="scroll"/>;
}
